import logoVertical from "../../assets/png/CodeNekt_for_Fleet_Logo.png";
import { CustomTextField } from "../../components/CustomTextField/CustomTextField";
import { useTranslation } from "react-i18next";
import { Checkbox, FormControlLabel } from "@mui/material";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { LoginDenial, useUserLogin } from "../../api/user";
import { useState } from "react";
import useStyles from "./styles";
import { colors } from "../../styles/colors";

type Props = {};

export const Login: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  /*
   * Refus d'acces : un ecran, pas un toast.
   *
   * L'identifiant et le mot de passe etaient bons, mais le compte n'ouvre
   * aucune flotte. C'est une situation ou la personne ne peut rien faire seule,
   * donc le message doit dire QUI contacter -- et il doit rester affiche. Un
   * toast disparait en quelques secondes et ne laisse rien a montrer, ni a
   * copier dans un mail au support. Un partenaire est reste bloque trois jours
   * exactement sur cet ecran, sans que personne ne sache ce qu'il voyait.
   */
  const [denial, setDenial] = useState<LoginDenial | null>(null);

  const { mutate: login, isPending } = useUserLogin(setDenial);

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
    // Toute saisie signifie une nouvelle tentative : le message precedent ne
    // decrit plus l'etat courant.
    if (denial) setDenial(null);
  };

  const handleLogin = () => {
    setDenial(null);
    login({ email: formData.email, password: formData.password });
  };

  const denialMessage =
    denial?.state === "pending_invitation"
      ? t(
          "login.pendingInvitation",
          "An invitation is waiting for this address but has not been accepted yet. Open the link in your invitation email, then sign in again."
        )
      : t(
          "login.noMembership",
          "Your account is not linked to any fleet yet. Ask your fleet manager to invite you, or contact support@codenektforfleet.com with the address you used."
        );

  return (
    <div className={classes.container}>
      <img src={logoVertical} alt="CodeNekt for Fleet" className={classes.logo} />
      <div className={classes.subContainer}>
        <CustomTextField
          placeholder={t("login.email", "Email")}
          fullWidth={true}
          onChange={(e) => handleInputChange("email", e.target.value)}
        />
        <CustomTextField
          placeholder={t("login.password", "Password")}
          type={"password"}
          pass
          fullWidth={true}
          onChange={(e) => handleInputChange("password", e.target.value)}
        />
        {denial && (
          <div className={classes.denialBox} role="alert">
            <CustomTypography
              variant={"link"}
              textDecoration={"none"}
              color={colors.WHITE}
              styles={{ containerText: classes.denialText }}
              text={denialMessage}
            />
          </div>
        )}
      </div>
      <div className={classes.subContainer2}>
        <FormControlLabel
          control={<Checkbox className={classes.customCheckbox} />}
          className={classes.customLabel}
          label={t("login.rememberMe", "Remember me")}
        />
        <CustomButton
          label={t("login.login", "Login")}
          height={"40px"}
          onClick={handleLogin}
          disabled={isPending}
          loading={isPending}
          variant={"white"}
        />
        <CustomTypography
          variant={"link"}
          textDecoration={"none"}
          to={"/enter_email_forgot_password"}
          color={colors.WHITE}
          styles={{ containerText: classes.forgotPassword }}
          text={t("login.forgotPassword", "Forgot password")}
        />
        {/* Entry point into the self-serve signup funnel. This is the only
            in-app link to /register, so removing it effectively disables
            self-serve onboarding for anyone without the direct URL. */}
        <CustomTypography
          variant={"link"}
          textDecoration={"none"}
          to={"/register"}
          color={colors.WHITE}
          styles={{ containerText: classes.forgotPassword }}
          text={t("register.createAccountCta", "Create an account")}
        />
      </div>
    </div>
  );
};
